import * as ENVS from '../../env.json';
import { usePromise } from '../../hooks/usePromise';
import { callFirebaseFunction } from '../firebaseFunctions';

// Split out of services/user/index.ts (2026-08) solely because
// `import.meta.env.DEV` below cannot be parsed by this app's Jest/@swc-jest
// setup (a raw SyntaxError, not a mock-able runtime issue — see
// services/experiences/templateValidation.test.ts's own jest.mock workaround
// for the same class of problem elsewhere). services/user/index.ts is a
// single CommonJS module as far as Jest is concerned, so ANY import from it
// — including totally unrelated ones like `matchUser` — used to drag this
// file's `import.meta` along and crash the whole test file at load time.
// Pure move, no behavior change; the one external call site
// (features/Users/pages/Profile/index.tsx) now imports from here directly.

const USER_ACCESS_TOKEN_CACHE: { [key: string]: Promise<string | null> } = {};

const _getUserAccessTokenForUserId = async (orgId: string, userId: string) => {
  try {
    return callFirebaseFunction<string | null>(
      'standaloneApp-getUserAccessTokenFromAdminApp',
      {
        userId,
        orgId
      }
    );
  } catch {
    return null;
  }
};

export const getUserAcessTokenForUserId = (orgId: string, userId: string) => {
  const cacheKey = [orgId, userId].join('|');
  if (!USER_ACCESS_TOKEN_CACHE[cacheKey]) {
    USER_ACCESS_TOKEN_CACHE[cacheKey] = _getUserAccessTokenForUserId(
      orgId,
      userId
    );
  }
  return USER_ACCESS_TOKEN_CACHE[cacheKey];
};

export const toEmployeeAppDashboardUrl = (
  userAccessToken: string,
  opts: {
    impersonate?: boolean; // defaults to true!
  } = {}
) => {
  const impersonate = opts.impersonate ?? true;
  const hostname = import.meta.env.DEV
    ? ENVS.domains.standaloneApp.local
    : ENVS.domains.standaloneApp.public;

  const u = new URL(hostname);
  u.pathname = `/v2/dashboard/${userAccessToken}`;
  if (impersonate) {
    u.searchParams.set('impersonate', 'true');
  }
  return u.toString();
};

export const useEmployeeAppDashboardUrlForUserId = (
  orgId: string,
  userId: string
) => {
  return usePromise(async () => {
    const userAccessToken = await getUserAcessTokenForUserId(orgId, userId);
    return userAccessToken ? toEmployeeAppDashboardUrl(userAccessToken) : null;
  }, [orgId, userId]);
};
